import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Card, Chip, IconButton } from '@mui/material';
import { useParams } from 'react-router-dom';

import { useThemeContext } from '../contexts/BaseThemeContextProvider';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { UserRecord } from '../types/patient';
import GradientCircularProgress from '../components/common/GradientCircularProgress';
import useBackendApi from '../hooks/useBackendApi';
import useDownloadGuide from '../hooks/useDownloadGuide';

const backendURL = process.env.REACT_APP_BACKEND_URL as string;

type PatientQuestionnaire = {
  questionnaire_id: string;
  submission_id: string;
  title: string;
  submitted_at: string;
  type: string;
  status: string;
};

type PatientQuestionnairesResponse = {
  questionnaires?: PatientQuestionnaire[] | null;
  message?: string;
};

type RouteParams = {
  org_path_name: string;
  acc_path_name: string;
  patient_user_id: string;
};

const QuestionnairesTable = () => {
  const { apiFetchRequest } = useBackendApi();

  const { theme, cognitoUserInfo, memberships, isMembershipDataReady } = useThemeContext();
  const recordsTheme = theme.custom.pages.frost.records;

  const { org_path_name, acc_path_name, patient_user_id } = useParams<RouteParams>();
  const [resolvedOrg, setResolvedOrg] = useState<string>('');
  const [resolvedAcc, setResolvedAcc] = useState<string>('');
  const [resolvedUser, setResolvedUser] = useState<string>('');

  const [records, setRecords] = useState<UserRecord<PatientQuestionnaire>[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [pdfRequest, setPdfRequest] = useState<{ url: string; filename: string } | null>(null);

  const fail = () => setErrorText('Unable to load questionnaires. Please try again.');

  const fetchQuestionnaires = useCallback(
    async (org: string, acc: string, user: string) => {
      setErrorText(null);
      setLoading(true);

      try {
        const endpoint = `${backendURL}/o/${encodeURIComponent(org)}/a/${encodeURIComponent(acc)}/patients/${encodeURIComponent(user)}/questionnaires`;
        const res = await apiFetchRequest({ endpoint, method: 'GET' });
        if (!res) return fail();

        const { status, body, isError } = res;

        const unauthorized =
          status === 401 ||
          body?.status_code === 401 ||
          body?.message === 'UNAUTHENTICATED' ||
          body?.message === 'UNAUTHORIZED' ||
          body?.message === 'NO_ACCESS';

        if (unauthorized) {
          window.location.href = '/401.html';
          return;
        }

        const patientNotFound =
          status === 404 ||
          body?.status_code === 404 ||
          body?.detail === 'Patient not found' ||
          body?.message === 'PATIENT_NOT_FOUND';

        if (patientNotFound) {
          setErrorText('Patient not found.');
          return;
        }

        if (isError || status !== 200) return fail();

        const rawList: PatientQuestionnaire[] = Array.isArray(body)
          ? (body as PatientQuestionnaire[])
          : Array.isArray((body as PatientQuestionnairesResponse)?.questionnaires)
            ? ((body as PatientQuestionnairesResponse).questionnaires as PatientQuestionnaire[])
            : [];

        const mapped: UserRecord<PatientQuestionnaire>[] = rawList.map((q, idx) => {
          const submissionId = q.submission_id.trim();
          const formId = q.questionnaire_id.trim();
          const qType = q.type.trim();
          const qTitle = q.title.trim();

          if (!submissionId || !formId || !qType || !qTitle) {
            fail();
          }

          return {
            id: submissionId,
            date: q.submitted_at,
            type: qType,
            title: qTitle,
            subtitle: `Form ID: ${formId}`,
            status: q.status,
            data: q,
          };
        });

        setRecords(mapped);
      } catch {
        fail();
      } finally {
        setLoading(false);
      }
    },
    [apiFetchRequest],
  );

  useEffect(() => {
    let org: string | undefined;
    let acc: string | undefined;
    let user: string | undefined;

    if (org_path_name && acc_path_name && patient_user_id) {
      org = org_path_name;
      acc = acc_path_name;
      user = patient_user_id;
    } else if (isMembershipDataReady) {
      if (memberships && memberships.length > 0) {
        const membership = memberships[0];
        const currentUserId = cognitoUserInfo?.currentUser?.username;
        org = membership.org_path_name;
        acc = membership.account_path_name;
        user = currentUserId;
      } else {
        setErrorText('We cannot retrieved questionnaires for you because you are not associated to an account.');
        setLoading(false);
        return;
      }
    }

    if (org && acc && user) {
      setResolvedOrg(org);
      setResolvedAcc(acc);
      setResolvedUser(user);
      void fetchQuestionnaires(org, acc, user);
    }
  }, [
    org_path_name,
    acc_path_name,
    patient_user_id,
    isMembershipDataReady,
    memberships,
    cognitoUserInfo,
    fetchQuestionnaires,
  ]);

  const {
    handleDownloadClick: handleFormPdfDownload,
    isGuideDownloading: isPdfDownloading,
    error: pdfDownloadError,
  } = useDownloadGuide(pdfRequest?.url ?? '', pdfRequest?.filename ?? 'form.pdf', { autoFetch: false });

  useEffect(() => {
    if (pdfRequest?.url) {
      void handleFormPdfDownload();
    }
  }, [pdfRequest, handleFormPdfDownload]);

  const handleRowClick = useCallback(
    (row: UserRecord<PatientQuestionnaire>) => {
      if (!resolvedOrg || !resolvedAcc || !resolvedUser) return;

      const submissionId = (row.data?.submission_id || row.id).trim();

      if (!submissionId) {
        setErrorText('Missing submission ID for navigation.');
        return;
      }

      const href = `/o/${encodeURIComponent(resolvedOrg)}/a/${encodeURIComponent(resolvedAcc)}/p/${encodeURIComponent(
        resolvedUser,
      )}/q/${encodeURIComponent(submissionId)}`;
      window.open(href, '_blank', 'noopener,noreferrer');
    },
    [resolvedOrg, resolvedAcc, resolvedUser, setErrorText],
  );

  const renderTypeBadge = useCallback(
    (type: string) => {
      let backgroundColor = theme.palette.primary.light;
      let color = theme.palette.primary.main;

      const badges = recordsTheme.recentActivity.badges;

      if (type === 'Result') {
        backgroundColor = badges.result.background;
        color = badges.result.color;
      } else if (type === 'Visit') {
        backgroundColor = badges.visit.background;
        color = badges.visit.color;
      } else if (type === 'Form') {
        backgroundColor = badges.form.background;
        color = badges.form.color;
      }

      return (
        <Chip
          label={type || '-'}
          size="small"
          sx={{
            backgroundColor,
            color,
            fontWeight: 600,
            fontSize: '0.75rem',
            borderRadius: '4px',
            height: '24px',
          }}
        />
      );
    },
    [theme, recordsTheme],
  );

  const renderStatus = useCallback(
    (status: string) => {
      const safe = status || '-';
      let icon: React.ReactNode = <CheckCircleOutlineIcon fontSize="small" sx={{ mr: 0.5 }} />;
      let color = recordsTheme.recentActivity.status.normal;

      if (safe === 'Completed') {
        icon = null;
        color = recordsTheme.recentActivity.status.completed;
      }

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', color, fontWeight: 500, fontSize: '0.875rem' }}>
          {icon}
          {safe}
        </Box>
      );
    },
    [recordsTheme],
  );

  return (
    <Card
      sx={{
        borderRadius: '16px',
        boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
        border: `1px solid ${recordsTheme.recentActivity.headerBackground}`,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: `1px solid ${recordsTheme.recentActivity.headerBackground}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: recordsTheme.recentActivity.title, fontSize: '1rem' }}>
          Questionnaires
        </Typography>
      </Box>

      {errorText || pdfDownloadError ? (
        <Box sx={{ px: 3, py: 2 }}>
          <Typography color="error">{errorText || pdfDownloadError}</Typography>
        </Box>
      ) : null}

      <Box sx={{ overflowX: 'auto' }}>
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <Box
            component="thead"
            sx={{
              backgroundColor: recordsTheme.recentActivity.headerBackground,
              color: recordsTheme.recentActivity.headerText,
              textTransform: 'uppercase',
              fontSize: '0.75rem',
            }}
          >
            <Box component="tr">
              <Box component="th" sx={{ px: 3, py: 1.5, textAlign: 'left', fontWeight: 500 }}>
                Date
              </Box>
              <Box component="th" sx={{ px: 3, py: 1.5, textAlign: 'left', fontWeight: 500 }}>
                Type
              </Box>
              <Box component="th" sx={{ px: 3, py: 1.5, textAlign: 'left', fontWeight: 500 }}>
                Description
              </Box>
              <Box component="th" sx={{ px: 3, py: 1.5, textAlign: 'left', fontWeight: 500 }}>
                Status
              </Box>
              <Box component="th" sx={{ px: 3, py: 1.5, textAlign: 'right', fontWeight: 500 }}>
                Action
              </Box>
            </Box>
          </Box>

          <Box
            component="tbody"
            sx={{
              '& tr:not(:last-child)': { borderBottom: `1px solid ${recordsTheme.recentActivity.headerBackground}` },
            }}
          >
            {loading ? (
              <Box component="tr">
                <Box
                  component="td"
                  colSpan={5}
                  sx={{ px: 3, py: 3, textAlign: 'center', color: recordsTheme.recentActivity.headerText }}
                >
                  <GradientCircularProgress
                    loadingMessage="Retrieving submissions..."
                    additionalCircularSxProps={{
                      width: '20px !important',
                      height: '20px !important',
                      margin: '16px',
                    }}
                    containerSxProps={{
                      width: '300px !important',
                      height: '100px !important',
                      display: 'inline-flex',
                    }}
                  />
                </Box>
              </Box>
            ) : records.length ? (
              records.map((row) => {
                const submissionId = (row.data?.submission_id || row.id).trim();
                const hasResolved = resolvedOrg && resolvedAcc && resolvedUser;
                const rowPdfUrl =
                  row.type !== 'Visit' && submissionId && hasResolved
                    ? `${backendURL}/o/${encodeURIComponent(resolvedOrg)}/a/${encodeURIComponent(resolvedAcc)}/patients/${encodeURIComponent(
                        resolvedUser,
                      )}/questionnaires/${encodeURIComponent(submissionId)}/pdf`
                    : null;
                const isRowDownloading = Boolean(isPdfDownloading && rowPdfUrl && pdfRequest?.url === rowPdfUrl);

                return (
                  <Box
                    component="tr"
                    key={row.id}
                    onClick={() => handleRowClick(row)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleRowClick(row);
                      }
                    }}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: recordsTheme.recentActivity.rowHover },
                      transition: 'background-color 0.2s',
                    }}
                  >
                    <Box
                      component="td"
                      sx={{ px: 3, py: 2, fontWeight: 500, color: recordsTheme.recentActivity.rowText }}
                    >
                      {row.date
                        ? new Date(row.date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : '-'}
                    </Box>

                    <Box component="td" sx={{ px: 3, py: 2 }}>
                      {renderTypeBadge(row.type)}
                    </Box>

                    <Box component="td" sx={{ px: 3, py: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: recordsTheme.recentActivity.rowText }}>
                        {row.title}
                      </Typography>
                    </Box>

                    <Box component="td" sx={{ px: 3, py: 2 }}>
                      {renderStatus(row.status || '')}
                    </Box>

                    <Box component="td" sx={{ px: 3, py: 2, textAlign: 'right' }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();

                          if (row.type === 'Visit') {
                            handleRowClick(row);
                            return;
                          }

                          if (!submissionId) {
                            setErrorText('Missing submission ID for PDF download.');
                            return;
                          }
                          if (!hasResolved) {
                            setErrorText('Missing required details for PDF download.');
                            return;
                          }
                          if (!rowPdfUrl) {
                            setErrorText('Unable to build PDF download URL.');
                            return;
                          }

                          setPdfRequest({ url: rowPdfUrl, filename: `${submissionId}.pdf` });
                        }}
                        disabled={isRowDownloading}
                        sx={{
                          color: recordsTheme.recentActivity.actionIcon,
                          '&:hover': { color: recordsTheme.recentActivity.actionIconHover },
                        }}
                      >
                        {row.type === 'Visit' ? (
                          <ChevronRightIcon />
                        ) : isRowDownloading ? (
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            Downloading...
                          </Typography>
                        ) : (
                          <PictureAsPdfIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Box>
                  </Box>
                );
              })
            ) : (
              <Box component="tr">
                <Box
                  component="td"
                  colSpan={5}
                  sx={{ px: 3, py: 3, textAlign: 'center', color: recordsTheme.recentActivity.headerText }}
                >
                  No questionnaires found.
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

export default QuestionnairesTable;
